<template>
    <div class="automation-message-filters">
        <div class="header">
            <h3>{{ $t("Mesaj Filtresi Yapılandırması") }}</h3>
            <a-button type="text" icon="close" @click="$emit('close')" />
        </div>
        <div class="content">
            <div class="filters-container">
                <!-- Author Filter -->
                <div class="filter-row">
                    <span class="filter-text">
                        <span class="prefix">Gönderen</span>
                        <InlineSelectOperator
                            v-model="localConfig.authorOperator"
                            :options="[{ label: 'eşittir', value: 'equal' }]"
                        />
                        <InlineSelectValue v-model="localConfig.author" :options="authorOptions" placeholder="seçin" />
                    </span>
                </div>

                <!-- Message Type Filter -->
                <div class="filter-row">
                    <span class="filter-text">
                        <span class="prefix">Mesaj tipi</span>
                        <InlineSelectOperator
                            v-model="localConfig.messageTypeOperator"
                            :options="[{ label: 'eşittir', value: 'equal' }]"
                        />
                        <InlineSelectValue
                            v-model="localConfig.messageType"
                            :options="messageTypeOptions"
                            placeholder="seçin"
                        />
                    </span>
                </div>

                <!-- Platform Filter -->
                <div class="filter-row">
                    <span class="filter-text">
                        <span class="prefix">Platform</span>
                        <InlineSelectOperator
                            v-model="localConfig.platformOperator"
                            :options="[{ label: 'eşittir', value: 'equal' }]"
                        />
                        <InlineSelectValue
                            v-model="localConfig.platform"
                            :options="platformOptions"
                            placeholder="seçin"
                        />
                    </span>
                </div>

                <!-- Channels Filter -->
                <div class="filter-row">
                    <span class="filter-text">
                        <span class="prefix">Kanallar</span>
                        <InlineSelectOperator
                            v-model="localConfig.channelsOperator"
                            :options="[
                                { label: 'içerir', value: 'contain' },
                                { label: 'tümü', value: 'all' },
                            ]"
                        />
                        <span v-if="localConfig.channelsOperator === 'contain'" class="multi-value-wrapper">
                            <a-select
                                v-model="localConfig.channels"
                                mode="multiple"
                                class="multi-select-inline"
                                placeholder="kanal seçin"
                            >
                                <a-select-option v-for="ch in allChannelsForPicker" :key="ch.value" :value="ch.value">
                                    {{ ch.label }}
                                </a-select-option>
                            </a-select>
                        </span>
                        <span v-else class="all-channels-text">tüm kanallar</span>
                    </span>
                </div>

                <!-- Text Filter -->
                <div class="filter-row">
                    <span class="filter-text">
                        <span class="prefix">Mesaj metni</span>
                        <InlineSelectOperator v-model="localConfig.textFilter.type" :options="textFilterTypeOptions" />
                        <InlineInput
                            v-if="!['empty', 'not_empty'].includes(localConfig.textFilter.type)"
                            v-model="localConfig.textFilter.value"
                            type="text"
                            placeholder="metin girin"
                        />
                    </span>
                </div>

                <!-- Case Sensitive -->
                <div class="filter-row" v-if="!['empty', 'not_empty'].includes(localConfig.textFilter.type)">
                    <span class="filter-text">
                        <a-checkbox v-model="localConfig.textFilter.caseSensitive">
                            Büyük/küçük harf duyarlı
                        </a-checkbox>
                    </span>
                </div>
            </div>

            <div class="config-info">
                <i class="material-icons">info</i>
                <span>Bu filtrelere uyan mesajlar geldiğinde otomasyon tetiklenecektir.</span>
            </div>

            <div class="form-actions d-flex gap-5 flex-end" style="margin-top: 20px">
                <SupsisButton @click="$emit('close')">İptal</SupsisButton>
                <SupsisButton primary @click="handleSave">Kaydet</SupsisButton>
            </div>
        </div>
    </div>
</template>

<script>
import SupsisButton from "../../../../../Settings/components/SupsisButton.vue";
import InlineSelectOperator from "../../../../components/InlineSelectOperator.vue";
import InlineSelectValue from "../../../../components/InlineSelectValue.vue";
import InlineInput from "../../../../components/InlineInput.vue";
import { AUTOMATION_API } from "../../../../../../../api/automation";

export default {
    name: "AutomationMessageFilters",
    components: {
        SupsisButton,
        InlineSelectOperator,
        InlineSelectValue,
        InlineInput,
    },
    props: {
        automation: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            localConfig: {
                author: "all",
                authorOperator: "equal",
                messageType: "all",
                messageTypeOperator: "equal",
                platform: "all",
                platformOperator: "equal",
                channels: [],
                channelsOperator: "all",
                textFilter: {
                    type: "contains",
                    value: "",
                    caseSensitive: false,
                },
            },
        };
    },
    computed: {
        channels() {
            return this.$store.state.channel.channels || [];
        },
        allChannelsForPicker() {
            return this.channels
                .filter((ch) => !ch.isDeleted)
                .map((ch) => ({
                    label: `${ch.name} (${this.getChannelTypeLabel(ch.type)})`,
                    value: ch._id,
                }));
        },
        authorOptions() {
            return [
                { label: "Tümü", value: "all" },
                { label: "Müşteri (Visitor)", value: "customer" },
                { label: "Temsilci", value: "agent" },
                { label: "Chatbot", value: "chatbot" },
            ];
        },
        messageTypeOptions() {
            return [
                { label: "Tümü", value: "all" },
                { label: "Metin", value: "text" },
                { label: "Tüm Medyalar", value: "media" },
                { label: "Resim", value: "image" },
                { label: "Video", value: "video" },
                { label: "Ses", value: "audio" },
                { label: "Dosya", value: "file" },
                { label: "Konum", value: "location" },
                { label: "JSON", value: "json" },
            ];
        },
        platformOptions() {
            return [
                { label: "Tümü", value: "all" },
                { label: "Web", value: "web" },
                { label: "WhatsApp", value: "whatsapp" },
                { label: "Instagram", value: "instagram" },
                { label: "Messenger", value: "messenger" },
                { label: "Telegram", value: "telegram" },
                { label: "LinkedIn", value: "linkedin" },
            ];
        },
        textFilterTypeOptions() {
            return [
                { label: "içerir", value: "contains" },
                { label: "içermez", value: "not_contains" },
                { label: "ile başlar", value: "starts_with" },
                { label: "ile biter", value: "ends_with" },
                { label: "tam eşleşir", value: "exact" },
                { label: "regex ile eşleşir", value: "regex" },
                { label: "boş", value: "empty" },
                { label: "dolu", value: "not_empty" },
            ];
        },
    },
    methods: {
        async handleSave() {
            try {
                const needsValue = !["empty", "not_empty"].includes(this.localConfig.textFilter.type);
                if (needsValue && !this.localConfig.textFilter.value) {
                    this.$message.error("Lütfen aranacak metni giriniz");
                    return;
                }

                await AUTOMATION_API.updateAutomationSingleKey(
                    this.automation._id,
                    "options.eventOptions",
                    this.localConfig
                );

                await this.$store.dispatch("automation/syncAutomations");
                this.$message.success("Mesaj filtresi yapılandırması güncellendi");
                this.$emit("updateAutomation");
            } catch (e) {
                this.$message.error("Güncelleme sırasında hata oluştu");
            }
        },
        loadConfig() {
            const currentConfig = (this.automation.options && this.automation.options.eventOptions) || {};
            this.localConfig = {
                author: currentConfig.author || "all",
                authorOperator: currentConfig.authorOperator || "equal",
                messageType: currentConfig.messageType || "all",
                messageTypeOperator: currentConfig.messageTypeOperator || "equal",
                platform: currentConfig.platform || "all",
                platformOperator: currentConfig.platformOperator || "equal",
                channels: currentConfig.channels || [],
                channelsOperator:
                    currentConfig.channelsOperator ||
                    (currentConfig.channels && currentConfig.channels.length > 0 ? "contain" : "all"),
                textFilter: {
                    type: (currentConfig.textFilter && currentConfig.textFilter.type) || "contains",
                    value: (currentConfig.textFilter && currentConfig.textFilter.value) || "",
                    caseSensitive: (currentConfig.textFilter && currentConfig.textFilter.caseSensitive) || false,
                },
            };
        },
        getChannelTypeLabel(type) {
            const typeLabels = {
                "whatsapp-business-api": "WhatsApp Business API",
                "whatsapp-business-api-supsis": "WhatsApp Business API",
                "whatsapp-v2": "WhatsApp V2",
                "whatsapp-unipile": "WhatsApp V3",
                instagram: "Instagram",
                messenger: "Messenger",
                "telegram-unipile": "Telegram",
                "linkedin-unipile": "LinkedIn",
                webchat: "Web Chat",
            };
            return typeLabels[type] || type;
        },
    },
    mounted() {
        this.loadConfig();
    },
};
</script>

<style scoped lang="scss">
.automation-message-filters {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    overflow: hidden;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid #e8e8e8;
        background: #fafafa;

        h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
        }
    }

    .content {
        padding: 20px;
        max-height: calc(100vh - 200px);
        overflow-y: auto;

        .filters-container {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 20px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            margin-bottom: 20px;

            .filter-row {
                display: flex;
                align-items: center;
                gap: 6px;
                margin-bottom: 12px;
                font-size: 15px;
                line-height: 32px;

                .filter-text {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    flex: 1;
                    flex-wrap: wrap;

                    .prefix {
                        color: #666;
                        font-weight: 500;
                        margin-right: 4px;
                    }

                    .multi-value-wrapper {
                        display: inline-flex;
                        align-items: center;
                    }

                    .all-channels-text {
                        color: #666;
                        font-style: italic;
                        padding: 0 8px;
                    }
                }
            }
        }

        .config-info {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px;
            background: #e6f7ff;
            border-radius: 6px;
            color: #1890ff;
            font-size: 13px;
            margin-bottom: 20px;

            .material-icons {
                font-size: 20px;
            }
        }
    }
}
</style>

<style lang="scss">
.automation-message-filters {
    .multi-select-inline {
        min-width: 200px !important;

        .ant-select-selection {
            border: none !important;
            border-bottom: 2px solid #52c41a !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: transparent !important;

            .ant-select-selection__rendered {
                margin: 0 !important;
                padding: 2px 8px !important;
                line-height: 28px !important;
            }

            .ant-select-selection__choice {
                background: #f6ffed !important;
                border: 1px solid #95de64 !important;
                color: #52c41a !important;
                font-weight: 500;
            }

            &:hover {
                background: #f6ffed !important;
            }
        }

        &.ant-select-focused .ant-select-selection {
            border-bottom-color: #389e0d !important;
            box-shadow: 0 2px 4px rgba(82, 196, 26, 0.2) !important;
        }
    }
}
</style>
